import React, { useEffect, useRef, useState } from "react";
import api from "../../services/api";

/**
 * Executa as tags <script> de um HTML recém-injetado.
 *
 * `innerHTML` insere as tags no DOM mas NUNCA as executa — é regra da
 * plataforma, não bug. Sem isto, todo HTML de plugin é obrigatoriamente
 * estático: nada de filtro, busca ou navegação interna, porque não existe um
 * único ponto onde o JS do plugin possa rodar. Trocar cada script por um clone
 * criado com `createElement` é o jeito padrão de contornar — o clone entra no
 * DOM como script "novo" e aí sim roda.
 *
 * Roda em ordem e de forma síncrona (sem `src` externo, que é o caso do HTML
 * vindo do plugin), então o script já encontra o HTML inteiro montado.
 */
function executarScripts(container) {
  const scripts = container.querySelectorAll("script");
  scripts.forEach((antigo) => {
    const novo = document.createElement("script");
    for (const attr of antigo.attributes) novo.setAttribute(attr.name, attr.value);
    novo.textContent = antigo.textContent;
    // Substitui no lugar: mantém a ordem original e evita rodar duas vezes.
    antigo.parentNode.replaceChild(novo, antigo);
  });
}

/**
 * Renderiza HTML cru de um plugin INLINE (dentro de um slot), no contexto do
 * app — sem iframe, sem clique. É o equivalente do openHtmlModal, mas embutido
 * no lugar do slot (ex: rodapé/conteúdo de uma mensagem).
 *
 * Fluxo: ao montar, faz POST no endpoint do plugin com o context; o plugin
 * devolve { html } (ou string). Injetamos via innerHTML. Se a action já trouxe
 * `html` estático, usa direto sem request.
 *
 * Props:
 *   routePath — rota isolada do plugin (/p/{hash}/{id})
 *   endpoint  — endpoint que devolve o HTML
 *   html      — HTML estático (opcional; dispensa o request)
 *   context   — dados enviados ao endpoint
 *   block     — ocupa a largura toda (conteúdo de aba/painel) em vez de fluir
 *               como um item inline junto de botões. O padrão continua inline
 *               porque o primeiro uso do componente foi dentro de rodapés.
 */
export default function PluginHtmlInline({
  routePath,
  endpoint,
  html: staticHtml,
  context,
  block = false,
}) {
  const ref = useRef(null);
  const [html, setHtml] = useState(staticHtml || "");

  useEffect(() => {
    let alive = true;
    if (staticHtml != null) {
      setHtml(staticHtml);
      return undefined;
    }
    if (!routePath || !endpoint) return undefined;
    api
      .post(`${routePath}${endpoint}`, context || {})
      .then(({ data }) => {
        if (!alive) return;
        const out = typeof data === "string" ? data : data.html ?? data.result ?? "";
        setHtml(out);
      })
      .catch(() => {
        if (alive) setHtml("");
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routePath, endpoint, staticHtml, JSON.stringify(context || {})]);

  useEffect(() => {
    if (!ref.current) return;
    ref.current.innerHTML = html || "";
    if (html) executarScripts(ref.current);
  }, [html]);

  if (!html) return null;
  // `data-plugin-route` é a rota isolada da empresa (/p/{hash}/{id}). O script
  // do plugin roda no DOM do app e não tem como saber o próprio caminho — o
  // hash é da empresa e o app é quem o resolve. Sem isto, uma tela de plugin
  // consegue LER (o HTML já vem pronto) mas não consegue chamar de volta o seu
  // próprio backend para gravar.
  if (block) return <div ref={ref} data-plugin-route={routePath} style={{ width: "100%" }} />;
  return (
    <span
      ref={ref}
      data-plugin-route={routePath}
      style={{ display: "inline-flex", alignItems: "center" }}
    />
  );
}
